import Vue from 'vue'
import * as types from '../mutation-type/checkout'
import checkoutService from '../services/checkout'
import { getActiveOrderId } from '~/helpers/active-order'

// The api hands back whole-group aggregates even when it only sends the products that changed,
// so these always overwrite on a merge.
const GROUP_AGGREGATE_KEYS = [
  'productIds', 'productsCount', 'units', 'total', 'subtotal',
  'preview_units', 'preview_total', 'short_quantity', 'available_to_ship'
]

const decorateGroup = group => ({
  ...group,
  checked: false,
  indeterminate: false,
  products: (group.products || []).map(product => decorateProduct(product))
})

// `previous` carries the row's client-only state across a refresh so a merge never clears a
// selection the user made mid-scan.
const decorateProduct = (product, previous = null) => ({
  ...product,
  checked: previous ? previous.checked : false,
  show_note: previous ? previous.show_note : false,
  show_edit_cost: previous ? previous.show_edit_cost : false
})

// state
export const state = {
  orderSummary: {
    productItems: []
  },
  // Server timestamp of the last summary fetch; echoed back to ask for a delta.
  orderSummarySyncedAt: null,
  // True while the summary is being fetched, so the page can say so instead of leaving the
  // previous order's rows on screen with no sign anything is happening.
  orderSummaryLoading: false,
  // Which order the rows currently on screen belong to. Switching orders must drop them;
  // refetching the same order must not, or a scan burst blanks the grid mid-scan.
  orderSummaryOrderId: null,
  // Catalog generation, carried on the summary response so the scanner's design map can tell
  // it has gone stale without polling for it.
  catalogVersion: null,
  selectedGroupBy: {
    key: 'brand/category',
    label: 'Brand/Category'
  },
  selectedSortBy: {
    key: 'design_number',
    label: 'Design #'
  },
  cardInfo: '',
  updatedCardInfo: '',
  deleteBulkProductFromOrderMessage: '',
  duplicateToOtherProductsMessage: '',
  creditCardDetail: '',
  fetchProductItemsOrderSummary: '',
  quantityExceed: {},
  orderReviewDeliveries : {}
}

// getters
export const getters = {
  orderSummary: state => state.orderSummary,
  orderSummaryLoading: state => state.orderSummaryLoading,
  cardInfo: state => state.cardInfo,
  updatedCardInfo: state => state.updatedCardInfo,
  deleteBulkProductFromOrderMessage: state => state.deleteBulkProductFromOrderMessage,
  duplicateToOtherProductsMessage: state => state.duplicateToOtherProductsMessage,
  creditCardDetail: state => state.creditCardDetail,
  selectedGroupBy: state => state.selectedGroupBy,
  quantityExceed: state => state.quantityExceed,
  orderReviewDeliveries: state => state.orderReviewDeliveries,
  catalogVersion: state => state.catalogVersion,
}

// mutation
export const mutations = {
  [types.SET_CHECKOUT_ORDER_SUMMARY_LOADING] (state, loading) {
    state.orderSummaryLoading = loading
  },
  [types.SET_CHECKOUT_ORDER_SUMMARY_ID] (state, orderId) {
    state.orderSummaryOrderId = orderId
  },
  [types.SET_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEMS] (state, { productItems, syncedAt = null }) {
    // A scan burst ends in a full reload. Rebuilding every row from scratch threw away the
    // rep's ticked boxes and closed any note or cost editor they had open — a refresh they
    // never asked for shouldn't undo their work, so carry that state across the replace.
    const previousProducts = new Map()
    const previousGroups = new Map()

    state.orderSummary.productItems.forEach(group => {
      previousGroups.set(group.groupByResult, {
        checked: group.checked,
        indeterminate: group.indeterminate
      })
      group.products.forEach(product => {
        if (product.checked || product.show_note || product.show_edit_cost) {
          previousProducts.set(product.id, product)
        }
      })
    })

    if (previousProducts.size || previousGroups.size) {
      productItems.forEach(group => {
        const previousGroup = previousGroups.get(group.groupByResult)
        if (previousGroup) {
          group.checked = previousGroup.checked
          group.indeterminate = previousGroup.indeterminate
        }
        group.products.forEach(product => {
          const previous = previousProducts.get(product.id)
          if (!previous) return
          product.checked = previous.checked
          product.show_note = previous.show_note
          product.show_edit_cost = previous.show_edit_cost
        })
      })
    }

    state.orderSummary.productItems = productItems
    state.orderSummarySyncedAt = syncedAt
  },
  [types.MERGE_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEMS] (state, { groups, syncedAt = null }) {
    groups.forEach(incoming => {
      const items = state.orderSummary.productItems
      const incomingIds = (incoming.products || []).map(product => product.id)

      // Group By = None keys on meta_data->id, which can come back empty; fall back to
      // whichever group already holds one of these products so we don't merge two into one.
      const index = incoming.groupByResult
        ? items.findIndex(group => group.groupByResult === incoming.groupByResult)
        : items.findIndex(group => group.products.some(product => incomingIds.includes(product.id)))

      if (index === -1) {
        items.push(decorateGroup(incoming))
        return
      }

      const existing = items[index]
      GROUP_AGGREGATE_KEYS.forEach(key => {
        if (key in incoming) Vue.set(existing, key, incoming[key])
      })

      ;(incoming.products || []).forEach(product => {
        const productIndex = existing.products.findIndex(item => item.id === product.id)
        if (productIndex === -1) {
          existing.products.push(decorateProduct(product))
          return
        }
        existing.products.splice(productIndex, 1, decorateProduct(product, existing.products[productIndex]))
      })
    })

    if (syncedAt) state.orderSummarySyncedAt = syncedAt
  },
  [types.SET_CATALOG_VERSION] (state, version) {
    state.catalogVersion = version
  },
  [types.TOGGLE_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEM_CHECKBOX] (state, { name, parentCategoryIndex, productIndex, value, setChildren }) {
    if (name !== undefined && name === 'parent-category') {
      if (value === undefined && typeof value !== 'boolean') {
        state.orderSummary.productItems[parentCategoryIndex].checked = !state.orderSummary.productItems[parentCategoryIndex].checked

        if (setChildren) {
          state.orderSummary.productItems[parentCategoryIndex].products.forEach(item => {
            item.checked = !item.checked
          })
        }

        return
      }

      state.orderSummary.productItems[parentCategoryIndex].checked = value

      if (setChildren) {
        state.orderSummary.productItems[parentCategoryIndex].products.forEach(item => {
          item.checked = value
        })
      }

      return
    }

    if (value === undefined && typeof value !== 'boolean') {
      state.orderSummary.productItems[parentCategoryIndex].products[productIndex].checked = !state.orderSummary.productItems[parentCategoryIndex].products[productIndex].checked
      return
    }

    state.orderSummary.productItems[parentCategoryIndex].products[productIndex].checked = value
  },
  [types.TOGGLE_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEM_PARENT_PROPERTY] (state, { index, propertyName = 'checked', value }) {
    state.orderSummary.productItems[index][propertyName] = value
  },
  [types.TOGGLE_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEM_CHILD_PROPERTY] (state, { parentIndex, index, propertyName = 'checked', value }) {
    state.orderSummary.productItems[parentIndex].products[index][propertyName] = value
  },
  [types.TOGGLE_CHECKOUT_ORDER_SUMMARY_SELECT_UNSELECT_CHECKBOX] (state, value) {
    state.orderSummary.productItems.forEach(parentCategory => {
      parentCategory.checked = value
      if (value) {
        parentCategory.indeterminate = false
      }
      parentCategory.products.forEach(productItem => {
        productItem.checked = value
      })
    })
  },
  [types.TOGGLE_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEM_PROPERTY] (state, { parentIndex, productIndex, property, value }) {
    if (value === undefined && typeof value !== 'boolean') {
      state.orderSummary.productItems[parentIndex].products[productIndex][property] = !state.orderSummary.productItems[parentIndex].products[productIndex][property]
      return
    }

    state.orderSummary.productItems[parentIndex].products[productIndex][property] = value
  },
  [types.SET_CHECKOUT_SELECTED_GROUP_BY] (state, payload) {
    state.selectedGroupBy = payload
  },
  [types.SET_CHECKOUT_SELECTED_SORT_BY] (state, payload) {
    state.selectedSortBy = payload
  },
  [types.CREATE_CARD_INFO] (state, payload) {
    state.cardInfo = payload
  },
  [types.UPDATE_CARD_INFO] (state, payload) {
    state.updatedCardInfo = payload
  },
  [types.DELETE_BULK_PRODUCT_FROM_ORDER] (state, payload) {
    state.deleteBulkProductFromOrderMessage = payload
  },
  [types.DUPLICATE_TO_OTHER_PRODUCTS] (state, payload) {
    state.duplicateToOtherProductsMessage = payload
  },
  [types.FETCH_CREDIT_CARD_DETAIL] (state, payload) {
    state.creditCardDetail = payload
  },
  [types.SET_QUANTITY_EXCEED] (state, { orderId, value }) {
      if (!state.quantityExceed[orderId]) {
          state.quantityExceed = { ...state.quantityExceed, [orderId]: value };
      }
  },
  [types.REMOVE_QUANTITY_EXCEED] (state, orderId) {
    const { [orderId]: _, ...rest} = state.quantityExceed;
    state.quantityExceed = rest;
  },
  [types.SET_ORDER_REVIEW_DELIVERIES] (state, deliveries) {
      state.orderReviewDeliveries = deliveries
  },
}

// actions
export const actions = {
  async fetchProductItemsOrderSummary ({ state, commit }, payload = null) {
    // Prefer an explicitly passed order id (e.g. the order-detail page targeting the viewed
    // order) over the globally-active draft order in localStorage.
    const activeDraftOrderId = payload || getActiveOrderId()

    // With no active order there is nothing to summarise, and asking anyway
    // sent `order_id=null` to the api for a guaranteed 422.
    if (!activeDraftOrderId) {
      commit(types.SET_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEMS, { productItems: [] })
      commit(types.SET_CHECKOUT_ORDER_SUMMARY_ID, null)
      return
    }

    // Is this a switch to a different order, or a refresh of the one already shown? Only a
    // switch drops the rows and raises the spinner. A refetch of the same order must do
    // neither: this path also runs on field blur, note and product deletes and mid-scan, and
    // blanking the grid (or flashing a blocking overlay) on those is what it used to do.
    const isOrderChange =
      state.orderSummaryOrderId !== null &&
      String(state.orderSummaryOrderId) !== String(activeDraftOrderId)

    // The header flips to the new order the moment it is picked, so leaving the previous
    // order's rows up reads as "this is the new order" — and their images linger longest.
    if (isOrderChange) {
      commit(types.SET_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEMS, { productItems: [] })
      commit(types.SET_CHECKOUT_ORDER_SUMMARY_LOADING, true)
    }

    try {
      const { data } = await checkoutService.fetchProductItemsOrderSummary(state.selectedGroupBy.key, state.selectedSortBy.key, activeDraftOrderId)
      commit(types.SET_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEMS, {
        productItems: (data.groups || []).map(decorateGroup),
        syncedAt: data.synced_at || null
      })
      commit(types.SET_CHECKOUT_ORDER_SUMMARY_ID, activeDraftOrderId)
      if (data.catalog_version !== undefined) commit(types.SET_CATALOG_VERSION, data.catalog_version)
      return data.totals || null
    } catch (error) {
      console.log('Error: ', error)
      return null
    } finally {
      if (isOrderChange) {
        commit(types.SET_CHECKOUT_ORDER_SUMMARY_LOADING, false)
      }
    }
  },
  /**
   * Pull only the rows touched since the last sync and merge them in. Backs the refresh that
   * runs *during* a scan burst, where refetching the whole cart every second is what made the
   * grid sit empty until the rep stopped scanning.
   */
  async fetchProductItemsOrderSummaryDelta ({ state, commit }, payload = null) {
    const activeDraftOrderId = payload || getActiveOrderId()

    // With no marker yet there is nothing to diff against — the caller should do a full fetch.
    if (!activeDraftOrderId || !state.orderSummarySyncedAt) {
      return null
    }

    try {
      const { data } = await checkoutService.fetchProductItemsOrderSummary(
        state.selectedGroupBy.key,
        state.selectedSortBy.key,
        activeDraftOrderId,
        state.orderSummarySyncedAt
      )
      commit(types.MERGE_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEMS, {
        groups: data.groups || [],
        syncedAt: data.synced_at || null
      })
      if (data.catalog_version !== undefined) commit(types.SET_CATALOG_VERSION, data.catalog_version)
      return data.totals || null
    } catch (error) {
      console.log('Error: ', error)
      return null
    }
  },
  async fetchDigitalCatalogSelectedProducts ({ commit }, params = {}) {
    try {
        // Set default parameter
        const queryParams = {
            is_order_placed: 0,
            ...params
        }
        const { data } = await checkoutService.getDigitalCatalogSelectedProducts(queryParams)
        // Create a single parent object containing all products
        const productItems = [{
            products: data.results.map(productItem => {
                return {
                    ...productItem,
                    show_note: false,
                }
            })
        }]
        commit(types.SET_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEMS, {
            productItems
        })
    } catch (error) {
        console.log('Error: ', error)
    }
  },

  async fetchOrderReviewDeliveries ({ commit }, orderId = null) {
    try {
        const { data } = await checkoutService.fetchOrderReview(orderId)
        commit(types.SET_ORDER_REVIEW_DELIVERIES, data)
    } catch (error) {
        console.error('Error fetching order review deliveries:', error)
        return {}
    }
  },

  async deleteBulkProductFromOrder ({ commit }, { orderId, productIds }) {
    try {
      const { data: { message } } = await checkoutService.deleteBulkProductFromOrder({ order_id: orderId, product_ids: productIds })
      commit(types.DELETE_BULK_PRODUCT_FROM_ORDER, message)
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async duplicateToOtherProducts ({ commit }, { orderId, fromProductId, toProductIds }) {
    const { data: { message } } = await checkoutService.duplicateToOtherProducts({
      order_id: orderId,
      from_product_id: fromProductId,
      to_product_ids: toProductIds
    })
    commit(types.DUPLICATE_TO_OTHER_PRODUCTS, message)
  },
  async createCardInfo ({ commit }, payload) {
    try {
      const response = await checkoutService.createCardInfo(payload)
      commit(types.CREATE_CARD_INFO, response)
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.response ? error.response.data.message : 'Unknown error' };
    }
  },
  async updateCardInfo ({ commit }, payload) {
    try {
      const response = await checkoutService.updateCardInfo(payload.id, payload.data)
      commit(types.UPDATE_CARD_INFO, response)
    } catch (error) {
      console.log(error)
    }
  },
  async deleteCardInfo ({ dispatch }, id) {
    try {
      await checkoutService.deleteCardInfo(id)
      commit(types.DELETE_CARD_INFO)
    } catch (error) {
      console.log(error)
    }
  },
  async fetchCreditCardsDetail ({ commit }, companyId) {
    try {
      const response = await checkoutService.fetchCreditCardsDetail(companyId)
      commit(types.FETCH_CREDIT_CARD_DETAIL, response)
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async orderHasProducts ({ dispatch }, { id, payload }) {
    try {
      await checkoutService.orderHasProducts(id, payload)
    } catch (error) {
      console.log('Error: ', error)
    }
  },
  async updateShipComplete ({ commit }, payload) {
    try {
        const response = await checkoutService.updateShipComplete(payload);
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error updating ship complete status:', error);
        return { success: false, error: error.response ? error.response.data.message : 'Unknown error' };
    }
  },
  setCheckoutSelectedGroupBy ({ commit }, payload) {
    commit(types.SET_CHECKOUT_SELECTED_GROUP_BY, payload)
  },
  setCheckoutSelectedSortBy ({ commit }, payload) {
    commit(types.SET_CHECKOUT_SELECTED_SORT_BY, payload)
  },
  toggleCheckoutOrderSummaryProductItemParentProperty ({ commit }, payload) {
    commit(types.TOGGLE_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEM_PARENT_PROPERTY, payload)
  },
  toggleCheckoutOrderSummaryProductItemCheckbox ({ commit }, payload) {
    commit(types.TOGGLE_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEM_CHECKBOX, payload)
  },
  toggleCheckoutOrderSummarySelectUnselectCheckbox ({ commit }, payload) {
    commit(types.TOGGLE_CHECKOUT_ORDER_SUMMARY_SELECT_UNSELECT_CHECKBOX, payload)
  },
  toggleCheckoutOrderSummaryProductItemChildProperty ({ commit }, payload) {
    commit(types.TOGGLE_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEM_CHILD_PROPERTY, payload)
  },
  toggleCheckoutOrderSummaryProductItemProperty ({ commit }, payload) {
    commit(types.TOGGLE_CHECKOUT_ORDER_SUMMARY_PRODUCT_ITEM_PROPERTY, payload)
  },
  setQuantityExceed({commit}, payload) {
    commit(types.SET_QUANTITY_EXCEED, payload);
  },
  removeQuantityExceed({ commit }, orderId) {
    commit(types.REMOVE_QUANTITY_EXCEED, orderId);
  }
}
